import { useEffect, useRef } from "react";
import { getSharedAudioContext } from "./useMediaRecorder";

type SilenceState = "waiting" | "speech" | "silence";

interface SilenceDetectionConfig {
  /** How long audio must stay below the threshold (after speech was detected) before auto-submitting. Default: 1200ms. */
  silenceMs?: number;
  /** The dBFS level that separates speech from silence. Audio below this is considered silent. Default: -50dB. */
  thresholdDb?: number;
  /** How long audio must stay above the threshold before it counts as real speech, arming the silence detector. Guards against brief noise (e.g. a click) triggering a premature auto-submit. Default: 300ms. */
  minSpeechMs?: number;
  /** A window at the very start of recording where silence is ignored entirely. Prevents an immediate auto-submit before the mic has a chance to pick up audio. Default: 300ms. */
  gracePeriodMs?: number;
}

export function useSilenceDetection(
  stream: MediaStream | null,
  enabled: boolean,
  onSilence: () => void,
  config?: SilenceDetectionConfig,
): void {
  const {
    silenceMs = 1200,
    thresholdDb = -50,
    minSpeechMs = 300,
    gracePeriodMs = 300,
  } = config ?? {};

  const onSilenceRef = useRef(onSilence);
  useEffect(() => {
    onSilenceRef.current = onSilence;
  }, [onSilence]);

  useEffect(() => {
    if (!enabled || !stream) return;

    const audioCtx = getSharedAudioContext();
    if (!audioCtx) return;

    let source: MediaStreamAudioSourceNode | null = null;
    let analyser: AnalyserNode | null = null;
    let rafId: number | null = null;
    let fired = false;

    let silenceState: SilenceState = "waiting";
    let speechStartTime: number | null = null;
    let silenceStartTime: number | null = null;
    const enabledAt = performance.now();

    try {
      source = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
    } catch {
      return;
    }

    const dataArray = new Float32Array(analyser.fftSize);

    function tick() {
      if (!analyser || fired) return;

      analyser.getFloatTimeDomainData(dataArray);

      let sumSq = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sumSq += dataArray[i] * dataArray[i];
      }
      const rms = Math.sqrt(sumSq / dataArray.length);
      const db = rms > 0 ? 20 * Math.log10(rms) : -Infinity;
      const isAboveThreshold = db > thresholdDb;
      const now = performance.now();
      const inGracePeriod = now - enabledAt < gracePeriodMs;

      if (silenceState === "waiting") {
        if (isAboveThreshold) {
          if (speechStartTime === null) {
            speechStartTime = now;
          } else if (now - speechStartTime >= minSpeechMs) {
            silenceState = "speech";
          }
        } else {
          speechStartTime = null;
        }
      } else if (silenceState === "speech") {
        if (!isAboveThreshold && !inGracePeriod) {
          if (silenceStartTime === null) {
            silenceStartTime = now;
          } else if (now - silenceStartTime >= silenceMs) {
            silenceState = "silence";
            fired = true;
            onSilenceRef.current();
            return;
          }
        } else if (isAboveThreshold) {
          silenceStartTime = null;
        }
      }

      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      source?.disconnect();
      analyser?.disconnect();
      // Do not close audioCtx — it is the shared singleton owned by useMediaRecorder.
    };
  }, [enabled, stream, silenceMs, thresholdDb, minSpeechMs, gracePeriodMs]);
}
